import { ReactNode, useCallback, useRef, useState } from 'react'

import { ArrowDownIcon, CopyIcon } from '@radix-ui/react-icons'

import useClipboard from 'hooks/use-clipboard'

import Box from 'components/atomic/atoms/box'
import { IconStyled } from 'components/atomic/atoms/icon'
import Text from 'components/atomic/atoms/text'
import Tooltip from 'components/atomic/atoms/tooltip'
import { PreStyled } from 'components/atomic/molecules/code-block'

type CodeProps = {
  children: ReactNode
  className?: string
}

// Code blocks longer than the upper threshold will be capped at the lower
// threshold. Having two levels avoids the case where a code block is just
// expanded by a few pixels when the content is just slightly too long. With
// this solution it expands at least 200px.
const SHOW_MORE_THRESHOLD = { LOWER: 500, UPPER: 700 }

const SharedCode = props => {
  const codeProps = props?.children?.props as CodeProps | undefined
  const preRef = useRef<HTMLPreElement>(null)
  const { hasCopied, onCopy } = useClipboard()
  const copy = () => onCopy(preRef.current?.textContent ?? '')

  const [contentLength, setExcessContentHidden] = useState<
    'within_threshold' | 'collapsed' | 'expanded'
  >('within_threshold')

  const codeRef = useCallback((node: HTMLElement | null) => {
    if (node && node.clientHeight > SHOW_MORE_THRESHOLD.UPPER) {
      setExcessContentHidden('collapsed')
    }
  }, [])

  return (
    <PreStyled
      ref={preRef}
      css={{
        position: 'relative',
        backgroundColor: '$bgCard',
        py: '$4',
        borderRadius: '$lg',
        border: '1px solid $bg3',

        ...(contentLength === 'collapsed' && {
          maxHeight: SHOW_MORE_THRESHOLD.LOWER,
          overflow: 'hidden',
        }),

        '& > div:first-child': {
          opacity: 0,
          transition: '.2s ease-in',
        },
        '&:hover > div:first-child': {
          opacity: 1,
        },
      }}
    >
      <Box
        absolute
        flex
        items="center"
        justify="between"
        css={{
          right: '$2',
          top: '$2',
        }}
      >
        <Tooltip text="Copy">
          <Box
            flex
            items="start"
            justify="start"
            as="button"
            aria-label={hasCopied ? 'Copied' : 'Copy'}
            onClick={copy}
          >
            {hasCopied ? (
              <Text size="xs" color="accent">
                Copied!
              </Text>
            ) : (
              <IconStyled as={CopyIcon} color="text2" size="md" />
            )}
          </Box>
        </Tooltip>
      </Box>
      <code {...codeProps} ref={codeRef} />

      {contentLength === 'collapsed' && (
        <Box
          as="button"
          absolute
          flex
          justify="center"
          items="end"
          css={{
            bottom: 0,
            left: 0,
            right: 0,
            top: '50%',
            pt: '$8',
            pb: '$2',
            px: '$5',
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0), $bgCard)',

            '&:hover > *': {
              color: '$text1',
            },
          }}
          onClick={() => setExcessContentHidden('expanded')}
        >
          <Text
            size="md"
            color="text2"
            weight="medium"
            css={{
              fontFamily: '$sans',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            Show more <ArrowDownIcon />
          </Text>
        </Box>
      )}
    </PreStyled>
  )
}

export default SharedCode
